import { cloneDeep, pick } from 'lodash-es'
import type { ICaptureSourceData } from '@/plugins/cad/module/CADModule/auxiliaryTool/captureHelper'
import {
  ColorMethod,
  type EntityType,
  LineType,
} from '@/plugins/cad/module/CADModule/model/common/constant'

/**
 * CAD 实体的公共契约字段。
 */
export interface IEntity {
  /** 实体全局唯一 id */
  id: string
  /** 图元类型 */
  type: EntityType
  /** 所属图层 id */
  layerId: string
  /** 颜色取值方式 */
  colorMethod: number
  /** 颜色值，格式由 colorMethod 决定 */
  color: number
  /** 线型 */
  lineType: LineType
  /** 线宽，单位毫米 */
  lineWidth: number
}

/**
 * 所有 CAD 实体的抽象基类，提供序列化与克隆基础能力。
 */
export abstract class BaseEntity implements IEntity {
  /** 实体全局唯一 id */
  id: string = ''
  /** 图元类型 */
  type: EntityType
  /** 所属图层 id */
  layerId: string = ''
  /** 颜色取值方式，默认随图层 */
  colorMethod: ColorMethod = ColorMethod.BY_LAYER
  /** 颜色值，格式由 colorMethod 决定 */
  color: number = 0xffffff
  /** 线型，默认实线 */
  lineType: LineType = LineType.Solid
  /** 线宽，单位毫米，默认 0.25 */
  lineWidth: number = 0.25

  // 保存的keys
  protected readonly baseSerializeKeys: string[] = [
    'id',
    'type',
    'layerId',
    'colorMethod',
    'color',
    'lineType',
    'lineWidth',
  ]

  protected abstract serializeKeys: string[]

  constructor(type: EntityType) {
    this.type = type
  }

  /**
   * 深拷贝当前实体。
   *
   * @returns 与当前实体字段完全一致的新实体
   */
  clone() {
    const cloned = cloneDeep(this)
    return cloned
  }

  /**
   * 获取实体的捕捉数据（点和线）
   * 捕捉点是CAD中用于精确定位的关键点，例如端点、中点、圆心等
   * 捕捉线用于最近点捕捉判断
   * 基类返回空数组，子类应重写此方法返回具体的捕捉数据
   * @returns 捕捉数据（包含点和线）
   */
  getCaptureData(): ICaptureSourceData {
    return {}
  }

  /**
   * 序列化实体，仅输出 baseSerializeKeys 与 serializeKeys 中声明的字段。
   *
   * @returns 可持久化的纯对象
   */
  serialize() {
    return cloneDeep(pick(this, [...new Set(this.baseSerializeKeys.concat(this.serializeKeys))]))
  }
}
